(() => {
  const qs = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  /* In-page section links (#pairs, #range, etc.) */
  const headerOffset = () => {
    const header = qs('.header');
    return (header?.offsetHeight || 72) + 12;
  };

  const scrollToHash = (hash, { updateHistory = true } = {}) => {
    const id = String(hash || '').replace(/^#/, '');
    if (!id) return false;

    const target = document.getElementById(id);
    if (!target) return false;

    const top = target.getBoundingClientRect().top + window.scrollY - headerOffset();
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    if (updateHistory) {
      history.replaceState(null, '', `#${id}`);
    }
    return true;
  };

  const hashFallbackUrl = (hash) => {
    if (hash === '#pairs' || hash === '#range') return `${window.Shopify?.routes?.root || '/'}collections/all`;
    return null;
  };

  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href*="#"]');
    if (!link) return;

    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin) return;

    const hash = url.hash;
    if (!hash || hash === '#') return;

    const currentPath = window.location.pathname.replace(/\/$/, '') || '/';
    const linkPath = url.pathname.replace(/\/$/, '') || '/';
    if (currentPath !== linkPath) return;

    if (scrollToHash(hash)) {
      e.preventDefault();
      return;
    }

    const fallback = hashFallbackUrl(hash);
    if (fallback) {
      e.preventDefault();
      window.location.href = fallback;
    }
  });

  if (window.location.hash) {
    requestAnimationFrame(() => {
      if (!scrollToHash(window.location.hash, { updateHistory: false })) {
        const fallback = hashFallbackUrl(window.location.hash);
        if (fallback) window.location.replace(fallback);
      }
    });
  }

  /* Mobile nav */
  const mobileNav = qs('[data-mobile-nav]');
  qsa('[data-mobile-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const open = mobileNav?.classList.toggle('is-open');
      document.body.style.overflow = open ? 'hidden' : '';
    });
  });

  /* Hero slider */
  qsa('[data-hero-slider]').forEach((root) => {
    const track = qs('[data-hero-track]', root);
    const slides = qsa('[data-hero-slide]', root);
    const dots = qsa('[data-hero-dot]', root);
    let index = 0;
    let timer;

    const go = (i) => {
      index = (i + slides.length) % slides.length;
      track.style.transform = `translateX(-${index * 100}%)`;
      dots.forEach((d, n) => d.classList.toggle('is-active', n === index));
      slides.forEach((s, n) => s.classList.toggle('is-active', n === index));
    };

    const next = () => go(index + 1);
    const prev = () => go(index - 1);

    qs('[data-hero-prev]', root)?.addEventListener('click', () => {
      prev();
      restart();
    });
    qs('[data-hero-next]', root)?.addEventListener('click', () => {
      next();
      restart();
    });
    dots.forEach((dot, i) => dot.addEventListener('click', () => {
      go(i);
      restart();
    }));

    const restart = () => {
      clearInterval(timer);
      timer = setInterval(next, 6000);
    };

    if (slides.length > 1) restart();

    root.addEventListener('mouseenter', () => clearInterval(timer));
    root.addEventListener('mouseleave', () => {
      if (slides.length > 1) restart();
    });
  });

  /* Range carousel — arrows scroll one card; no autoplay */
  const setupRangeCarousel = (root) => {
    const viewport = qs('.range-carousel__viewport', root);
    const track = qs('[data-range-track]', root);
    const cards = qsa('.range-card-wa', root);
    if (!viewport || !track || !cards.length) return;

    const gap = 16;
    const visibleCount = () => (window.innerWidth < 900 ? 2 : 4);

    const layout = () => {
      const count = visibleCount();
      const width = Math.max(140, Math.floor((viewport.clientWidth - gap * (count - 1)) / count));
      cards.forEach((card) => {
        card.style.flex = `0 0 ${width}px`;
        card.style.width = `${width}px`;
      });
      return width;
    };

    const step = () => (cards[0]?.getBoundingClientRect().width || layout()) + gap;

    const maxScroll = () => Math.max(0, viewport.scrollWidth - viewport.clientWidth);

    const scrollByCard = (dir) => {
      layout();
      const amount = step();
      const max = maxScroll();
      let next = viewport.scrollLeft + dir * amount;
      if (dir > 0 && next >= max - 8) next = max;
      if (dir < 0 && next <= 8) next = 0;
      viewport.scrollTo({ left: Math.max(0, Math.min(max, next)), behavior: 'smooth' });
    };

    qs('[data-range-prev]', root)?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      scrollByCard(-1);
    });
    qs('[data-range-next]', root)?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      scrollByCard(1);
    });

    window.addEventListener('resize', layout);
    layout();
  };

  qsa('[data-range-carousel]').forEach(setupRangeCarousel);

  /* Editor tabs */
  qsa('[data-editor-tabs]').forEach((root) => {
    const tabs = qsa('[data-editor-tab]', root);
    const panels = qsa('[data-editor-panel]', root);
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const id = tab.getAttribute('data-editor-tab');
        tabs.forEach((t) => t.classList.toggle('is-active', t === tab));
        panels.forEach((p) => {
          p.hidden = p.getAttribute('data-editor-panel') !== id;
        });
      });
    });
  });

  /* Legacy tabs */
  qsa('[data-tabs]').forEach((root) => {
    const buttons = qsa('[data-tab]', root);
    const panels = qsa('[data-tab-panel]', root);
    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-tab');
        buttons.forEach((b) => b.classList.toggle('is-active', b === btn));
        panels.forEach((p) => {
          p.hidden = p.getAttribute('data-tab-panel') !== id;
        });
      });
    });
  });

  /* Promo grid — Shopify-hosted videos */
  qsa('[data-promo-video]').forEach((card) => {
    const video = qs('video', card);
    const play = qs('[data-promo-play]', card);
    if (!video || !play) return;

    const sync = () => {
      card.classList.toggle('is-playing', !video.paused && !video.ended);
    };

    play.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (video.paused) {
        qsa('[data-promo-video] video').forEach((other) => {
          if (other !== video) other.pause();
        });
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });

    video.addEventListener('play', sync);
    video.addEventListener('pause', sync);
    video.addEventListener('ended', sync);
  });

  /* Look cards — cycle hair angles */
  qsa('[data-video-card]').forEach((card) => {
    const frames = qsa('[data-angle-frame]', card);
    const buttons = qsa('[data-angle]', card);
    const play = qs('[data-video-play]', card);
    if (!frames.length) return;

    let index = 0;
    let timer;

    const show = (i) => {
      index = (i + frames.length) % frames.length;
      frames.forEach((frame, n) => frame.classList.toggle('is-active', n === index));
      buttons.forEach((btn, n) => btn.classList.toggle('is-active', n === index));
    };

    const stop = () => {
      clearInterval(timer);
      timer = null;
      card.classList.remove('is-playing');
    };

    const start = () => {
      card.classList.add('is-playing');
      clearInterval(timer);
      timer = setInterval(() => show(index + 1), 1400);
    };

    play?.addEventListener('click', () => {
      if (timer) stop();
      else start();
    });

    qs('.video-card__stage', card)?.addEventListener('click', (e) => {
      if (e.target.closest('[data-angle], [data-video-play]')) return;
      if (timer) stop();
    });

    buttons.forEach((btn, i) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        show(i);
        if (timer) {
          clearInterval(timer);
          timer = setInterval(() => show(index + 1), 1400);
        }
      });
    });
  });

  /* Cart drawer */
  const drawer = qs('[data-cart-drawer]');
  const openDrawer = () => {
    drawer?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  };
  const closeDrawer = () => {
    drawer?.classList.remove('is-open');
    document.body.style.overflow = '';
  };

  /* Search modal */
  const searchModal = qs('[data-search-drawer]');
  const searchInput = qs('[data-search-input]', searchModal);
  const openSearch = () => {
    searchModal?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    searchModal?.setAttribute('aria-hidden', 'false');
    searchInput?.focus();
  };
  const closeSearch = () => {
    searchModal?.classList.remove('is-open');
    document.body.style.overflow = '';
    searchModal?.setAttribute('aria-hidden', 'true');
  };

  qsa('[data-search-open]').forEach((el) =>
    el.addEventListener('click', (e) => {
      e.preventDefault();
      openSearch();
    })
  );
  qsa('[data-search-close]').forEach((el) => el.addEventListener('click', closeSearch));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeSearch();
  });

  qsa('[data-cart-open]').forEach((el) => el.addEventListener('click', (e) => {
    e.preventDefault();
    openDrawer();
  }));
  qsa('[data-cart-close]').forEach((el) => el.addEventListener('click', closeDrawer));

  /* Cart remove + quantity */
  const updateCart = (key, quantity) => {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) }),
    })
      .then((r) => r.json())
      .then(() => location.reload());
  };

  qsa('[data-cart-remove]').forEach((btn) => {
    btn.addEventListener('click', () => updateCart(btn.getAttribute('data-cart-remove'), 0));
  });

  qsa('[data-cart-qty-change]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const key = btn.getAttribute('data-cart-qty-change');
      const delta = parseInt(btn.getAttribute('data-delta'), 10);
      const valEl = qs(`[data-cart-qty-value="${key}"]`);
      const current = valEl ? parseInt(valEl.textContent, 10) : 1;
      updateCart(key, current + delta);
    });
  });

  /* Product gallery thumbs + arrows */
  qsa('[data-product-gallery]').forEach((gallery) => {
    const main = qs('[data-gallery-main]', gallery);
    const thumbs = qsa('[data-gallery-thumb]', gallery);
    const show = (i) => {
      if (!thumbs.length) return;
      const index = (i + thumbs.length) % thumbs.length;
      const thumb = thumbs[index];
      const src = thumb.getAttribute('data-src');
      if (main && src) {
        main.removeAttribute('srcset');
        main.src = src;
      }
      thumbs.forEach((t, n) => t.classList.toggle('is-active', n === index));
    };
    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => show(i));
    });
    qs('[data-gallery-prev]', gallery)?.addEventListener('click', () => {
      const current = thumbs.findIndex((t) => t.classList.contains('is-active'));
      show((current < 0 ? 0 : current) - 1);
    });
    qs('[data-gallery-next]', gallery)?.addEventListener('click', () => {
      const current = thumbs.findIndex((t) => t.classList.contains('is-active'));
      show((current < 0 ? 0 : current) + 1);
    });
  });

  /* Product variant picker */
  qsa('[data-product-form]').forEach((root) => {
    const jsonEl = qs('[data-product-json]', root);
    if (!jsonEl) return;
    let productData;
    try {
      productData = JSON.parse(jsonEl.textContent);
    } catch {
      return;
    }
    const formatMoney = (cents) => {
      const amount = (Number(cents) / 100).toFixed(2);
      const fmt = window.Shopify?.money_format || '£{{amount}}';
      return fmt.replace(/\{\{\s*amount\s*\}\}/, amount);
    };
    const idInput = qs('[data-variant-id]', root);
    const priceEl = qs('[data-product-price]', root);
    const compareEl = qs('[data-product-compare]', root);
    const saveEl = qs('[data-product-save]', root);
    const addBtn = qs('[data-add]', root);
    const mainImg = qs('[data-gallery-main]', root);
    const afterpayEl = qs('[data-afterpay-message]', root);

    const findVariant = () => {
      const opts = qsa('input[type=radio]:checked', root).map((input) => input.value);
      if (!opts.length) return productData.variants?.[0];
      return productData.variants.find((variant) => variant.options.every((opt, i) => opt === opts[i])) || null;
    };

    const apply = () => {
      const variant = findVariant();
      if (!variant) {
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.textContent = 'Unavailable';
        }
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (priceEl) {
        priceEl.textContent = formatMoney(variant.price);
        priceEl.classList.toggle('product__price--sale', onSale);
      }
      const onSale = root.dataset.featuredSale === 'true'
        && variant.compare_at_price
        && variant.compare_at_price > variant.price;
      if (compareEl) {
        compareEl.hidden = !onSale;
        if (onSale) compareEl.textContent = formatMoney(variant.compare_at_price);
      }
      if (saveEl) {
        saveEl.hidden = !onSale;
        if (onSale) saveEl.textContent = `-${Math.round((1 - variant.price / variant.compare_at_price) * 100)}%`;
      }
      if (addBtn) {
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available ? 'Add to cart' : 'Sold out';
      }
      if (mainImg && variant.featured_image) {
        mainImg.src = variant.featured_image.src;
      }
      if (afterpayEl) {
        const installmentEl = qs('strong', afterpayEl);
        if (installmentEl) installmentEl.textContent = formatMoney(Math.round(variant.price / 4));
        afterpayEl.dataset.amount = String(variant.price);
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      history.replaceState({}, '', url);
    };

    qsa('input[type=radio]', root).forEach((input) => input.addEventListener('change', apply));
  });

  /* Quantity steppers */
  qsa('[data-qty]').forEach((wrap) => {
    const input = qs('input', wrap);
    qs('[data-qty-minus]', wrap)?.addEventListener('click', () => {
      input.value = Math.max(1, Number(input.value || 1) - 1);
    });
    qs('[data-qty-plus]', wrap)?.addEventListener('click', () => {
      input.value = Number(input.value || 1) + 1;
    });
  });

  /* Product review modal */
  const REVIEWS_STORAGE_KEY = 'nuvelle_product_reviews_v2';

  const readStoredReviews = () => {
    try {
      const raw = localStorage.getItem(REVIEWS_STORAGE_KEY);
      const data = raw ? JSON.parse(raw) : {};
      return data && typeof data === 'object' ? data : {};
    } catch {
      return {};
    }
  };

  const writeStoredReviews = (byProduct) => {
    try {
      localStorage.setItem(REVIEWS_STORAGE_KEY, JSON.stringify(byProduct));
    } catch {
      /* storage full or unavailable */
    }
  };

  const reviewFingerprint = (review) => [
    review.productId || '',
    review.name || '',
    review.title || '',
    review.content || '',
    String(review.rating || ''),
  ].join('|');

  const dedupeReviews = (reviews) => {
    const seen = new Set();
    return reviews.filter((review) => {
      const key = review.id || reviewFingerprint(review);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const getLocalReviewsForProduct = (productId) => {
    const store = readStoredReviews();
    const list = store[String(productId)] || [];
    return list.map((r) => ({ ...r, productId: String(productId) }));
  };

  const saveLocalReview = (review) => {
    const productId = String(review.productId || '');
    if (!productId) return;
    const store = readStoredReviews();
    const list = store[productId] || [];
    const entry = {
      id: review.id || `local-${Date.now()}`,
      productId,
      productTitle: review.productTitle || '',
      rating: Number(review.rating) || 5,
      title: review.title || '',
      content: review.content || '',
      image: review.image || '',
      name: review.name || 'Customer',
      createdAt: review.createdAt || new Date().toISOString(),
    };
    store[productId] = dedupeReviews([entry, ...list]);
    writeStoredReviews(store);
    return entry;
  };

  const parseReviewFromDom = (el) => ({
    id: el.dataset.reviewId || reviewFingerprint({
      productId: el.closest('[data-product-reviews]')?.dataset.productId,
      name: qs('.product__review-card-name', el)?.textContent,
      title: qs('.product__review-card-title', el)?.textContent,
      content: qs('.product__review-card-body', el)?.textContent,
      rating: el.dataset.rating,
    }),
    productId: String(el.closest('[data-product-reviews]')?.dataset.productId || ''),
    rating: Number(el.dataset.rating) || 5,
    title: qs('.product__review-card-title', el)?.textContent || '',
    content: qs('.product__review-card-body', el)?.textContent || '',
    image: qs('.product__review-card-photo img', el)?.src || '',
    name: qs('.product__review-card-name', el)?.textContent || 'Customer',
    createdAt: el.dataset.createdAt || new Date().toISOString(),
  });

  const getManualReviewsFromPanel = (panel) => {
    const feedList = qs('[data-review-feed-list]', panel);
    if (!feedList) return [];
    return qsa('[data-manual-review]', feedList).map(parseReviewFromDom);
  };

  const getServerReviewsFromPanel = (panel) => {
    const feedList = qs('[data-review-feed-list]', panel);
    if (!feedList) return [];
    return qsa('[data-published-review]:not([data-manual-review])', feedList).map(parseReviewFromDom);
  };

  const mergePanelReviews = (panel, extra = []) => {
    const productId = String(panel.dataset.productId || '');
    return dedupeReviews([
      ...getManualReviewsFromPanel(panel),
      ...getServerReviewsFromPanel(panel),
      ...getLocalReviewsForProduct(productId),
      ...extra,
    ].filter((r) => String(r.productId) === productId));
  };

  const compressReviewImage = (file) => new Promise((resolve) => {
    if (!file || !String(file.type || '').startsWith('image/')) {
      resolve('');
      return;
    }
    const maxDim = 480;
    const maxBytes = 42000;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        if (width > maxDim || height > maxDim) {
          if (width >= height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        let quality = 0.82;
        let dataUrl = canvas.toDataURL('image/jpeg', quality);
        while (dataUrl.length > maxBytes && quality > 0.35) {
          quality -= 0.08;
          dataUrl = canvas.toDataURL('image/jpeg', quality);
        }
        resolve(dataUrl.length <= maxBytes ? dataUrl : '');
      };
      img.onerror = () => resolve('');
      img.src = reader.result;
    };
    reader.onerror = () => resolve('');
    reader.readAsDataURL(file);
  });

  const parseReviewComment = (comment) => {
    const body = comment?.body || comment?.content || '';
    const parts = body.split('---');
    const metaRaw = (parts[0] || '').trim();
    const content = (parts[1] || '').trim();
    if (!metaRaw) return null;
    try {
      const meta = JSON.parse(metaRaw);
      if (!meta.product_id) return null;
      return {
        id: String(comment.id || metaRaw),
        productId: String(meta.product_id),
        productTitle: meta.product_title || '',
        rating: Number(meta.rating) || 5,
        title: meta.title || '',
        content,
        image: meta.image || '',
        name: comment.author || 'Customer',
        email: comment.email || '',
        createdAt: comment.created_at || new Date().toISOString(),
        published: true,
      };
    } catch {
      return null;
    }
  };

  const starsHtml = (rating) => {
    const n = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    return '★'.repeat(n) + '☆'.repeat(5 - n);
  };

  const escapeHtml = (str) => {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  };

  const renderReviewCard = (review) => `
    <article class="product__review-card" data-published-review data-rating="${review.rating}">
      <div class="product__review-card-head">
        <div class="product__review-card-identity">
          ${review.image ? `<div class="product__review-card-photo"><img src="${review.image}" alt="Customer photo" loading="lazy" width="48" height="48"></div>` : ''}
          <span class="product__review-card-name">${escapeHtml(review.name || 'Customer')}</span>
        </div>
        <span class="product__review-card-stars" aria-label="${review.rating} out of 5 stars">${starsHtml(review.rating)}</span>
      </div>
      ${review.title ? `<p class="product__review-card-title">${escapeHtml(review.title)}</p>` : ''}
      ${review.content ? `<p class="product__review-card-body">${escapeHtml(review.content)}</p>` : ''}
    </article>
  `;

  const renderCustomerPhotoGrid = (panel, publishedReviews) => {
    const photosWrap = qs('[data-review-photos]', panel);
    const grid = qs('[data-review-photo-grid]', panel);
    if (!photosWrap || !grid) return;

    const existing = qsa('.product__reviews-photo:not(.product__reviews-photo--more) img', grid)
      .map((img) => img.getAttribute('src'))
      .filter((src) => src);

    const fromReviews = publishedReviews
      .map((r) => r.image)
      .filter((src) => typeof src === 'string' && src.length > 0);

    const images = [...new Set([...existing, ...fromReviews])];

    if (!images.length) {
      photosWrap.hidden = true;
      grid.innerHTML = '';
      return;
    }

    const maxVisible = 4;
    const visible = images.slice(0, maxVisible);
    const remaining = images.length - visible.length;

    grid.innerHTML = visible
      .map((src) => `<div class="product__reviews-photo"><img src="${src}" alt="Customer photo" loading="lazy" width="200" height="200"></div>`)
      .join('');

    if (remaining > 0) {
      grid.innerHTML += `
        <div class="product__reviews-photo product__reviews-photo--more" aria-label="${remaining} more customer photos">
          <img src="${visible[visible.length - 1]}" alt="" loading="lazy" width="200" height="200">
          <span>+${remaining}</span>
        </div>
      `;
    }

    photosWrap.hidden = false;
  };

  const submitReviewToShopify = (review) => {
    const body = [
      'Product review submission',
      `Product: ${review.productTitle}`,
      `URL: ${review.productUrl}`,
      `Rating: ${review.rating}/5`,
      `Title: ${review.title || '—'}`,
      '',
      review.content,
    ].join('\n');

    const formData = new FormData();
    formData.append('form_type', 'contact');
    formData.append('utf8', '✓');
    formData.append('contact[name]', review.name);
    formData.append('contact[email]', review.email);
    formData.append('contact[body]', body);

    fetch(`${window.Shopify?.routes?.root || '/'}contact`, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
    }).catch(() => {});
  };

  const submitReviewComment = (review, commentUrl) => {
    const metaPayload = {
      product_id: Number(review.productId),
      product_title: review.productTitle,
      rating: review.rating,
      title: review.title || '',
    };
    if (review.image) metaPayload.image = review.image;

    const meta = JSON.stringify(metaPayload);
    const commentBody = `${meta}\n---\n${review.content}`;

    const formData = new FormData();
    formData.append('form_type', 'new_comment');
    formData.append('utf8', '✓');
    formData.append('comment[author]', review.name);
    formData.append('comment[email]', review.email);
    formData.append('comment[body]', commentBody);

    return fetch(commentUrl, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
    });
  };

  const fetchPublishedReviews = async (panel) => {
    const jsonUrl = panel.dataset.reviewsCommentsJson;
    if (!jsonUrl) return [];
    try {
      const res = await fetch(jsonUrl, { credentials: 'same-origin' });
      if (!res.ok) return [];
      const data = await res.json();
      const productId = String(panel.dataset.productId || '');
      return (data.comments || [])
        .map(parseReviewComment)
        .filter((r) => r && String(r.productId) === productId);
    } catch {
      return [];
    }
  };

  const getManualStats = (panel) => ({
    count: Number(panel.dataset.manualCount || 0),
    score: parseFloat(panel.dataset.manualScore || '0'),
    stars: {
      5: Number(panel.dataset.manualStar5 || 0),
      4: Number(panel.dataset.manualStar4 || 0),
      3: Number(panel.dataset.manualStar3 || 0),
      2: Number(panel.dataset.manualStar2 || 0),
      1: Number(panel.dataset.manualStar1 || 0),
    },
  });

  const updateReviewPanel = (panel, publishedReviews) => {
    const manual = getManualStats(panel);
    const starCounts = { ...manual.stars };

    publishedReviews.forEach((r) => {
      const bucket = Math.max(1, Math.min(5, Number(r.rating) || 5));
      starCounts[bucket] += 1;
    });

    const totalCount = manual.count + publishedReviews.length;
    const publishedSum = publishedReviews.reduce((sum, r) => sum + Number(r.rating || 0), 0);
    const manualSum = manual.score * manual.count;
    const avgScore = totalCount > 0
      ? ((manualSum + publishedSum) / totalCount).toFixed(1)
      : '0.0';

    const scoreEl = qs('.product__reviews-score', panel);
    const countEl = qs('.product__reviews-count', panel);
    const starsEl = qs('.product__reviews-stars', panel);
    if (scoreEl) scoreEl.textContent = avgScore;
    if (countEl) countEl.textContent = `${totalCount} reviews`;
    if (starsEl) {
      starsEl.textContent = starsHtml(Math.round(parseFloat(avgScore)));
      starsEl.classList.toggle('product__reviews-stars--empty', totalCount === 0);
    }

    qsa('.product__reviews-bar', panel).forEach((bar) => {
      const numEl = qs('.product__reviews-bar-num', bar);
      const fillEl = qs('.product__reviews-bar-fill', bar);
      const idx = qsa('.product__reviews-bar', panel).indexOf(bar);
      const starLevel = 5 - idx;
      const count = starCounts[starLevel] || 0;
      const pct = totalCount > 0 ? Math.round((count / totalCount) * 100) : 0;
      if (numEl) numEl.textContent = String(count);
      if (fillEl) fillEl.style.width = `${pct}%`;
    });

    const ratingLine = qs('.product__rating-line', document);
    if (ratingLine) {
      const lineStars = qs('.product__rating-line-stars', ratingLine);
      const lineScore = qs('.product__rating-line-score', ratingLine);
      const lineCount = qs('.product__rating-line-count', ratingLine);
      if (lineStars) {
        lineStars.textContent = starsHtml(Math.round(parseFloat(avgScore)));
        lineStars.classList.toggle('product__reviews-stars--empty', totalCount === 0);
      }
      if (lineScore) lineScore.textContent = avgScore;
      if (lineCount) lineCount.textContent = `(${totalCount} reviews)`;
    }

    const feed = qs('[data-review-feed]', panel);
    const feedList = qs('[data-review-feed-list]', panel);
    const actions = qs('[data-review-actions]', panel);
    if (!feed || !feedList) return;

    if (!publishedReviews.length) {
      feed.hidden = true;
      feedList.innerHTML = '';
      if (actions) actions.hidden = true;
      setReviewFeedOpen(panel, false);
      renderCustomerPhotoGrid(panel, publishedReviews);
      applyReviewPagination(panel);
      return;
    }

    if (actions) actions.hidden = false;
    feedList.innerHTML = publishedReviews
      .slice()
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      .map((r) => renderReviewCard(r))
      .join('');

    renderCustomerPhotoGrid(panel, publishedReviews);
    applyReviewPagination(panel);
  };

  const setReviewFeedOpen = (panel, open) => {
    const feed = qs('[data-review-feed]', panel);
    const actions = qs('[data-review-actions]', panel);
    const toggleBtns = qsa('[data-review-toggle]', panel);
    if (!feed) return;
    const hasReviews = qsa('[data-published-review]', feed).length > 0;
    feed.hidden = !(open && hasReviews);
    if (actions) actions.hidden = !hasReviews || open;
    toggleBtns.forEach((btn) => {
      if (btn.classList.contains('product__reviews-view-btn')) {
        btn.setAttribute('aria-expanded', open && hasReviews ? 'true' : 'false');
        btn.textContent = open ? 'Hide reviews' : 'View reviews';
      }
    });
  };

  const applyReviewPagination = (panel) => {
    const feed = qs('[data-review-feed]', panel);
    const feedList = qs('[data-review-feed-list]', panel);
    const pager = qs('[data-review-pager]', panel);
    const status = qs('[data-review-page-status]', panel);
    const prevBtn = qs('[data-review-page-prev]', panel);
    const nextBtn = qs('[data-review-page-next]', panel);
    if (!feed || !feedList) return;

    const cards = qsa('[data-published-review]', feedList);
    const perPage = Math.max(1, Number(feed.dataset.reviewsPerPage || 2));
    const totalPages = Math.max(1, Math.ceil(cards.length / perPage));
    let page = Number(feed.dataset.reviewPage || 1);
    if (page > totalPages) page = totalPages;
    if (page < 1) page = 1;
    feed.dataset.reviewPage = String(page);

    cards.forEach((card, index) => {
      const cardPage = Math.floor(index / perPage) + 1;
      card.hidden = cardPage !== page;
    });

    if (pager) {
      pager.hidden = cards.length <= perPage;
    }
    if (status) status.textContent = `${page} / ${totalPages}`;
    if (prevBtn) prevBtn.disabled = page <= 1;
    if (nextBtn) nextBtn.disabled = page >= totalPages;
  };

  const initReviewFeedControls = (panel) => {
    if (panel.dataset.reviewFeedReady === 'true') return;
    panel.dataset.reviewFeedReady = 'true';

    qsa('[data-review-toggle]', panel).forEach((btn) => {
      btn.addEventListener('click', () => {
        const feed = qs('[data-review-feed]', panel);
        const isOpen = feed && !feed.hidden;
        setReviewFeedOpen(panel, !isOpen);
        if (!isOpen) {
          feed?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    });

    qs('[data-review-page-prev]', panel)?.addEventListener('click', () => {
      const feed = qs('[data-review-feed]', panel);
      if (!feed) return;
      feed.dataset.reviewPage = String(Math.max(1, Number(feed.dataset.reviewPage || 1) - 1));
      applyReviewPagination(panel);
    });

    qs('[data-review-page-next]', panel)?.addEventListener('click', () => {
      const feed = qs('[data-review-feed]', panel);
      if (!feed) return;
      feed.dataset.reviewPage = String(Number(feed.dataset.reviewPage || 1) + 1);
      applyReviewPagination(panel);
    });

    setReviewFeedOpen(panel, false);
    applyReviewPagination(panel);
  };

  const refreshReviewPanel = async (panel) => {
    const server = await fetchPublishedReviews(panel);
    const merged = dedupeReviews([
      ...getManualReviewsFromPanel(panel),
      ...server,
      ...getLocalReviewsForProduct(panel.dataset.productId || ''),
    ]);
    updateReviewPanel(panel, merged);
  };

  const initReviewModal = () => {
    const modal = qs('[data-review-modal]');
    if (!modal) return;

    const form = qs('[data-review-form]', modal);
    const ratingInput = qs('[data-review-rating-input]', modal);
    const starButtons = qsa('[data-star-value]', modal);
    const steps = {
      1: qs('[data-review-step="1"]', modal),
      2: qs('[data-review-step="2"]', modal),
      success: qs('[data-review-step="success"]', modal),
    };

    let selectedRating = Number(ratingInput?.value || 5);
    let selectedImage = '';

    const photoInput = qs('[data-review-photo-input]', modal);
    const photoTrigger = qs('[data-review-photo-trigger]', modal);
    const photoField = qs('.review-modal__photo-field', modal);
    const photoPreview = qs('[data-review-photo-preview]', modal);
    const photoPreviewImg = qs('[data-review-photo-preview-img]', modal);

    const clearPhoto = () => {
      selectedImage = '';
      if (photoInput) photoInput.value = '';
      if (photoPreview) photoPreview.hidden = true;
      if (photoPreviewImg) photoPreviewImg.removeAttribute('src');
      if (photoField) photoField.classList.remove('has-photo');
    };

    photoTrigger?.addEventListener('click', () => photoInput?.click());

    if (photoInput) {
      photoInput.addEventListener('change', async () => {
        const file = photoInput.files?.[0];
        if (!file) {
          clearPhoto();
          return;
        }
        const dataUrl = await compressReviewImage(file);
        if (!dataUrl) {
          clearPhoto();
          return;
        }
        selectedImage = dataUrl;
        if (photoPreviewImg) photoPreviewImg.src = dataUrl;
        if (photoPreview) photoPreview.hidden = false;
        if (photoField) photoField.classList.add('has-photo');
      });
    }

    qs('[data-review-photo-remove]', modal)?.addEventListener('click', clearPhoto);

    const paintStars = (value, hover = false) => {
      starButtons.forEach((btn) => {
        const v = Number(btn.getAttribute('data-star-value'));
        btn.classList.toggle('is-active', v <= value);
        btn.classList.toggle('is-hover', hover && v <= value);
      });
    };

    const setRating = (value) => {
      selectedRating = Math.max(1, Math.min(5, value));
      if (ratingInput) ratingInput.value = String(selectedRating);
      paintStars(selectedRating);
    };

    const showStep = (name) => {
      Object.entries(steps).forEach(([key, el]) => {
        if (!el) return;
        el.hidden = key !== name;
      });
    };

    const openModal = () => {
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      showStep('1');
      setRating(5);
      clearPhoto();
      form?.reset();
      if (ratingInput) ratingInput.value = '5';
      qs('#review-content', modal)?.focus();
    };

    const closeModal = () => {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    qsa('[data-review-open]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openModal();
      });
    });

    qsa('[data-review-close]', modal).forEach((el) => {
      el.addEventListener('click', closeModal);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.hidden) closeModal();
    });

    starButtons.forEach((btn) => {
      const value = Number(btn.getAttribute('data-star-value'));
      btn.addEventListener('click', () => setRating(value));
      btn.addEventListener('mouseenter', () => paintStars(value, true));
      btn.addEventListener('mouseleave', () => paintStars(selectedRating));
    });

    qs('[data-review-next]', modal)?.addEventListener('click', () => {
      const content = qs('#review-content', modal);
      if (!content?.value.trim()) {
        content?.focus();
        content?.setAttribute('aria-invalid', 'true');
        return;
      }
      content?.removeAttribute('aria-invalid');
      showStep('2');
      qs('#review-name', modal)?.focus();
    });

    qs('[data-review-back]', modal)?.addEventListener('click', () => showStep('1'));

    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const submitBtn = qs('[data-review-submit]', modal);
      if (submitBtn?.disabled) return;

      const name = qs('#review-name', modal)?.value.trim();
      const email = qs('#review-email', modal)?.value.trim();
      const title = qs('#review-title', modal)?.value.trim();
      const content = qs('#review-content', modal)?.value.trim();
      if (!name || !email || !content) return;

      const productId = qs('input[name="product_id"]', form)?.value || '';
      const productTitle = qs('input[name="product_title"]', form)?.value || '';
      const productUrl = qs('input[name="product_url"]', form)?.value || window.location.pathname;

      const review = {
        productId,
        productTitle,
        productUrl,
        rating: selectedRating,
        title,
        content,
        image: selectedImage,
        name,
        email,
        createdAt: new Date().toISOString(),
      };

      const panel = qs('[data-product-reviews]');
      const commentUrl = panel?.dataset.reviewsCommentUrl;

      submitBtn.disabled = true;

      const savedReview = saveLocalReview(review);
      if (panel && savedReview) {
        updateReviewPanel(panel, mergePanelReviews(panel, [savedReview]));
        const feed = qs('[data-review-feed]', panel);
        if (feed) feed.dataset.reviewPage = '1';
        setReviewFeedOpen(panel, true);
        applyReviewPagination(panel);
      }

      if (commentUrl) {
        try {
          await submitReviewComment(review, commentUrl);
        } catch {
          /* local display still works */
        }
      }

      submitReviewToShopify(review);

      submitBtn.disabled = false;
      showStep('success');

      if (panel) {
        setTimeout(() => refreshReviewPanel(panel), 1500);
        setTimeout(() => refreshReviewPanel(panel), 5000);
      }
    });

    qsa('[data-product-reviews]').forEach((panel) => {
      initReviewFeedControls(panel);
      updateReviewPanel(panel, mergePanelReviews(panel));
      if (panel.dataset.reviewsCommentsJson) refreshReviewPanel(panel);
    });
  };

  initReviewModal();

  const initContactForm = () => {
    qsa('.contact-form').forEach((form) => {
      const btn = qs('[data-contact-submit]', form);
      const notice = qs('[data-contact-notice]', form);
      const defaultLabel = btn?.dataset.defaultLabel || 'Send message';

      const showNotice = (message, type) => {
        if (!notice) return;
        notice.textContent = message;
        notice.hidden = false;
        notice.classList.toggle('contact-form__notice--success', type === 'success');
        notice.classList.toggle('contact-form__notice--error', type === 'error');
        notice.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      };

      const setSending = (sending) => {
        if (!btn) return;
        btn.disabled = sending;
        btn.textContent = sending ? 'Sending…' : defaultLabel;
        btn.classList.toggle('is-sending', sending);
      };

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (btn?.disabled) return;

        setSending(true);
        if (notice) {
          notice.hidden = true;
          notice.classList.remove('contact-form__notice--success', 'contact-form__notice--error');
        }

        const formData = new FormData(form);
        formData.set('form_type', 'contact');
        formData.set('utf8', '✓');

        try {
          const res = await fetch(`${window.Shopify?.routes?.root || '/'}contact`, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin',
            redirect: 'follow',
          });

          if (res.ok) {
            form.reset();
            showNotice('Your message has been sent. We’ll reply soon!', 'success');
          } else {
            showNotice('Something went wrong. Please try again or email us directly.', 'error');
          }
        } catch {
          showNotice('Something went wrong. Please try again or email us directly.', 'error');
        } finally {
          setSending(false);
        }
      });
    });
  };

  initContactForm();
})();
